import React, { useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, IconButton, Typography, Box,
  CircularProgress, Alert, InputAdornment, Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { updateUserPassword } from '../services/adminUserService';

const MIN_PW = 8;

/**
 * PasswordDialog
 * Props: open, onClose, user
 */
const PasswordDialog = ({ open, onClose, user }) => {
  const [pw, setPw]           = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow]       = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const reset = () => { setPw(''); setConfirm(''); setError(''); setShow(false); };

  const handleClose = () => { reset(); onClose(); };

  const validate = () => {
    if (!pw) return 'Password is required.';
    if (pw.length < MIN_PW) return `Password must be at least ${MIN_PW} characters.`;
    if (pw !== confirm) return 'Passwords do not match.';
    return null;
  };

  const handleSave = useCallback(async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setSaving(true);
    try {
      const userId = user?.Id || user?.id;
      await updateUserPassword(userId, pw);
      toast.success('Password updated successfully');
      handleClose();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to update password';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }, [pw, confirm, user]); // eslint-disable-line

  const strength = pw.length === 0 ? 0 : pw.length < 8 ? 1 : pw.length < 12 ? 2 : 3;
  const strengthColors = ['', '#f44336', '#ff9800', '#4caf50'];
  const strengthLabels = ['', 'Weak', 'Fair', 'Strong'];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockIcon sx={{ fontSize: 18, color: '#667eea' }} />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>Reset Password</Typography>
            {user && (
              <Typography variant="caption" color="text.secondary">
                {user.Name || user.Email}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField
          fullWidth size="small" label="New Password"
          type={show ? 'text' : 'password'}
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShow((v) => !v)}>
                  {show ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Strength bar */}
        {pw.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
              {[1, 2, 3].map((lvl) => (
                <Box
                  key={lvl}
                  sx={{
                    flex: 1, height: 4, borderRadius: 2,
                    bgcolor: strength >= lvl ? strengthColors[strength] : '#e0e0e0',
                    transition: 'background-color 0.2s',
                  }}
                />
              ))}
            </Box>
            <Typography variant="caption" sx={{ color: strengthColors[strength], fontWeight: 600 }}>
              {strengthLabels[strength]}
            </Typography>
          </Box>
        )}

        <TextField
          fullWidth size="small" label="Confirm Password"
          type={show ? 'text' : 'password'}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={confirm.length > 0 && pw !== confirm}
          helperText={confirm.length > 0 && pw !== confirm ? 'Passwords do not match' : ''}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
        />
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        <Button onClick={handleClose} color="inherit" sx={{ fontWeight: 600, textTransform: 'none', borderRadius: 1.5 }}>
          Cancel
        </Button>
        <Button
          variant="contained" onClick={handleSave}
          disabled={saving || !pw || !confirm}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <LockIcon sx={{ fontSize: 16 }} />}
          sx={{
            fontWeight: 700, textTransform: 'none', borderRadius: 1.5,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)' },
            '&:disabled': { background: '#e0e0e0' },
          }}
        >
          {saving ? 'Updating…' : 'Update Password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasswordDialog;
